import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useModules } from '../context/FeatureContext';

// One list of "what needs you", shared by the header bell and anything else that shows the same
// counts, so the two can never disagree. It refreshes when the window regains focus and every
// minute while the page is visible (the bell used to load once and never update).
//
// Each item: { id, label, count, severity: 'danger' | 'warning' | 'info', to }
const count = (r) => (Array.isArray(r.data) ? r.data.length
  : Array.isArray(r.data?.data) ? r.data.data.length
  : Number(r.data?.count) || Number(r.data?.total) || 0);

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export default function useAttention({ intervalMs = 60000 } = {}) {
  const { user } = useAuth() || {};
  const modules = useModules() || {};
  // Aged care reminders, for the people who can act on them in an organisation that has the module.
  const agedCare = ['admin', 'office_user', 'super_admin'].includes(user?.role) && modules.aged_care === true;
  const [items, setItems] = useState([]);
  const [loadedAt, setLoadedAt] = useState(null);
  const alive = useRef(true);

  const load = useCallback(async () => {
    const [ts, inc, lv, att, ac] = await Promise.all([
      api.get('/timesheets?status=pending').then(count).catch(() => 0),
      api.get('/incidents?status=open').then(count).catch(() => 0),
      api.get('/leave?status=pending').then(count).catch(() => 0),
      api.get('/dashboard/attendance').then((r) => r.data).catch(() => null),
      agedCare ? api.get('/aged-care/attention/summary').then((r) => r.data).catch(() => null) : Promise.resolve(null),
    ]);
    if (!alive.current) return;
    const next = [];
    // Clock-in / clock-out problems (see components/AttendanceAlerts, on the dashboard).
    const notOut = att?.notClockedOut?.length || 0;
    const noIn = att?.missedClockIn?.length || 0;
    if (notOut > 0) next.push({ id: 'not-clocked-out', label: `${plural(notOut, 'shift', 'shifts')} not clocked out`, count: notOut, severity: 'danger', to: '/' });
    const autoOut = att?.autoClockedOut?.length || 0;
    if (autoOut > 0) next.push({ id: 'auto-clocked-out', label: `${plural(autoOut, 'automatic clock-out', 'automatic clock-outs')} to check`, count: autoOut, severity: 'warning', to: '/timesheets' });
    if (noIn > 0) next.push({ id: 'missed-clock-in', label: `${plural(noIn, 'missed clock-in', 'missed clock-ins')}`, count: noIn, severity: 'warning', to: '/' });
    if (inc > 0) next.push({ id: 'incidents', label: `${plural(inc, 'open incident', 'open incidents')} to review`, count: inc, severity: 'danger', to: '/incidents' });
    if (ts > 0) next.push({ id: 'timesheets', label: `${plural(ts, 'timesheet', 'timesheets')} waiting for approval`, count: ts, severity: 'warning', to: '/timesheets' });
    if (lv > 0) next.push({ id: 'leave', label: `${plural(lv, 'leave request', 'leave requests')} to review`, count: lv, severity: 'warning', to: '/leave' });
    if (ac && (ac.overdue > 0 || ac.soon > 0)) {
      const parts = [ac.overdue ? `${ac.overdue} overdue` : null, ac.soon ? `${ac.soon} due this week` : null].filter(Boolean).join(', ');
      next.push({ id: 'aged-care', label: `Aged care: ${parts}${ac.mine ? ` (${ac.mine} yours)` : ''}`, count: ac.overdue + ac.soon, severity: ac.overdue ? 'danger' : 'warning', to: '/aged-care' });
    }
    setItems(next);
    setLoadedAt(new Date());
  }, [agedCare]);

  useEffect(() => {
    alive.current = true;
    load();
    const onFocus = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, intervalMs);
    return () => {
      alive.current = false;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      clearInterval(t);
    };
  }, [load, intervalMs]);

  return { items, total: items.reduce((n, i) => n + i.count, 0), loadedAt, reload: load };
}
